import { COMMENT_AGENTS, buildComment } from '../../extensions/bundled/task-manager/commentMentions';
import { commentHtmlWithAttachments } from './commentAttachments';
import { PERSONAL } from '../scopes';
import { mcpError } from '../errors';
import { scopeReport } from '../writeScope';
import { stageBoardRecords } from '../boardChanges';
import { newId, nowIso, patchedRecord, dataOf, toWireRecord } from '../boardRecords';
import { boardRows, casWrite, findBoard, findTask, revsFor, targetScope } from './boardWriteChecks';

/** Bounded CAS retries for the append path — read, hash, write, three times. */
export const COMMENT_ATTEMPTS = 3;

/** The agent id from the bus context's `caller`, if it is one we know. */
export function commentAgentFromCaller(caller) {
  const id = caller && typeof caller === 'object' ? caller.agent : null;
  return typeof id === 'string' && Object.prototype.hasOwnProperty.call(COMMENT_AGENTS, id) ? id : null;
}

/**
 * Append a comment to a task.
 *
 * Comments are nested inside the task record, so this is the realistic way to
 * hit the 32 KB per-record outbox cap: past it, the plain upsert path would
 * store the row and silently un-sync that task forever. The CAS path refuses
 * instead, and the tool description tells the model not to retry.
 *
 * `expected_rev` is not an argument: an append's answer to concurrency is the
 * retry loop, exactly as `document_write`'s append arm does it. Read-concat
 * without a CAS would be a lost update.
 */
export async function boardCommentAdd(
  { board_id: boardId, task_id: taskId, body, scope, author_name: authorName, attachments },
  ctx = {}
) {
  // Which agent is writing: the MCP client's identity, set by Rust from the
  // wire (client_agent.rs) and handed in NEXT TO the arguments — a model can
  // put anything into `author_name`, but not into this.
  const agent = commentAgentFromCaller(ctx.caller);
  const wire = scope || PERSONAL;
  const text = String(body ?? '');
  if (!text.trim()) {
    return mcpError('invalid_argument', 'body must not be empty.', { field: 'body' });
  }
  // Images / videos from the media upload tool (card 7de79076): a rich comment, the
  // same shape the app's composer stores. Checked before any read or write.
  let bodyHtml = null;
  if (attachments != null && !(Array.isArray(attachments) && attachments.length === 0)) {
    const built = commentHtmlWithAttachments(text, attachments, {
      docUid: `${taskId}-cmt`,
      scope: wire,
      hint: 'This file was uploaded for another task or document. Upload it again with this task_id (and scope), then pass that attachment.',
    });
    if (built.error) return mcpError('invalid_argument', built.error.message, { field: built.error.field });
    bodyHtml = built.html;
  }

  const target = targetScope(wire);
  if (target.error) return target.error;

  let lastError = null;
  for (let attempt = 0; attempt < COMMENT_ATTEMPTS; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const rows = await boardRows(target.scopeKey, boardId);
    const boardRow = findBoard(rows, boardId);
    if (!boardRow) return mcpError('not_found', 'No board with that id in this scope.');
    const taskRow = findTask(rows, taskId);
    if (!taskRow) return mcpError('not_found', 'No task with that id on this board.');

    // eslint-disable-next-line no-await-in-loop
    const revs = await revsFor(target.scopeKey, boardId);
    const rev = revs.get(taskRow.id) ?? null;

    const comment = buildComment({
      id: newId(),
      body: text,
      // A plain string, never translated: it is stored data a teammate reads,
      // not UI copy. `authorId` stays null because attributing an agent's
      // comment to the signed-in user is a lie the user's colleagues would act
      // on. `picked: []` makes `mentions` [] structurally — see the header.
      authorName: authorName ? String(authorName) : (agent && COMMENT_AGENTS[agent]) || 'MCP client',
      authorId: null,
      authorAgent: agent,
      bodyHtml,
      picked: [],
    });
    const existing = Array.isArray(dataOf(taskRow).comments) ? dataOf(taskRow).comments : [];
    const record = patchedRecord(taskRow, {
      data: { comments: [...existing, comment], updatedAt: nowIso() },
    });

    // eslint-disable-next-line no-await-in-loop
    const out = await casWrite(target.scopeKey, record, rev);
    if (!out.error) {
      const result = {
        boardId: boardRow.id,
        taskId: taskRow.id,
        id: comment.id,
        authorName: comment.authorName,
        createdAt: comment.createdAt,
        commentCount: record.data.comments.length,
        scope: wire,
        rev: out.rev,
        ...scopeReport(wire),
      };
      return stageBoardRecords(result, target.scopeKey, [toWireRecord(record)]);
    }
    // Only a lost race is worth retrying. `record_too_large` will fail
    // identically forever and `write_failed` is not a race.
    if (out.error.error !== 'conflict') return out.error;
    lastError = out.error;
  }
  return (
    lastError || mcpError('conflict', 'The task kept changing while commenting; read it again.')
  );
}
